import React, { useState } from 'react';
import { Container, Typography, Grid, Card, CardMedia, CardContent, CardActions, Button, Dialog, DialogTitle, DialogContent, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Fade } from '@mui/material';
import ariesImage from '../images/aries.png';
import taurusImage from '../images/taurus.png';
import geminiImage from '../images/gemini.png';
import cancerImage from '../images/cancer.png';
import leoImage from '../images/leo.png';
import virgoImage from '../images/virgo.png';
import libraImage from '../images/libra.png';
import scorpioImage from '../images/scorpio.png';
import sagittariusImage from '../images/sagitarius.png';
import capricornImage from '../images/capricon.png';
import aquariusImage from '../images/aquarius.png';
import piscesImage from '../images/pisces.png';

// Mapping zodiak to images
const zodiacImages = {
  Aries: ariesImage,
  Taurus: taurusImage,
  Gemini: geminiImage,
  Cancer: cancerImage,
  Leo: leoImage,
  Virgo: virgoImage,
  Libra: libraImage,
  Scorpio: scorpioImage,
  Sagittarius: sagittariusImage,
  Capricorn: capricornImage,
  Aquarius: aquariusImage,
  Pisces: piscesImage,
};

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  boxShadow: theme.shadows[3],
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledCardMedia = styled(CardMedia)({
  height: 200, // Increased height for better image display
  objectFit: 'cover',
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const ZodiacPersonality = () => {
  const [selectedZodiac, setSelectedZodiac] = useState(null);

  const zodiacData = [
    { name: 'Aries', date: '21 Maret - 19 April', positive: 'Berani, ambisius, percaya diri, penuh energi, antusias, penuh gairah', negative: 'Impulsif, tidak sabar, ceroboh, egois, mudah marah', strengths: 'Pemimpin alami, berani, optimis', weaknesses: 'Pengambilan keputusan yang impulsif, kurang sabar, kecenderungan untuk mendominasi', career: 'Pengusaha, pemimpin, manajer, politikus, tenaga penjual', relationship: 'Aries cenderung bersemangat dan penuh gairah dalam hubungan. Mereka menghargai kemerdekaan dan dapat cemburu.' },
    { name: 'Taurus', date: '20 April - 20 Mei', positive: 'Teguh, praktis, setia, sabar, memiliki nilai-nilai tinggi', negative: 'Keras kepala, lambat bergerak, cenderung menempel', strengths: 'Stabil, dapat diandalkan, sabar', weaknesses: 'Kekakuan, keras kepala, cenderung materialistis', career: 'Akuntan, artis, arsitek, petani', relationship: 'Taurus sangat setia dan mendalam dalam hubungan, tetapi mereka bisa sangat keras kepala.' },
    { name: 'Gemini', date: '21 Mei - 20 Juni', positive: 'Pintar, energik, ramah, fleksibel', negative: 'Gampang bosan, tidak konsisten, tidak stabil', strengths: 'Cepat beradaptasi, cerdas, komunikatif', weaknesses: 'Kurang konsisten, gampang bosan, terlalu analitis', career: 'Jurnalis, pengacara, guru, penulis', relationship: 'Gemini adalah pasangan yang cerdas dan penuh semangat tetapi bisa jadi tidak stabil.' },
    { name: 'Cancer', date: '21 Juni - 22 Juli', positive: 'Penuh perhatian, intuitif, empatik, pelindung', negative: 'Emosional, cenderung menghindar, terlalu sensitif', strengths: 'Peduli, intuitif, setia', weaknesses: 'Terlalu emosional, cenderung menghindar, terlalu protektif', career: 'Perawat, konselor, pengacara', relationship: 'Cancer sangat perhatian dan pelindung dalam hubungan, namun bisa sangat emosional.' },
    { name: 'Leo', date: '23 Juli - 22 Agustus', positive: 'Karismatik, percaya diri, penuh semangat, kreatif', negative: 'Egois, dominan, suka perhatian', strengths: 'Kepemimpinan, kreativitas, percaya diri', weaknesses: 'Egois, dominasi, kurang fleksibel', career: 'Aktor, manajer, pemimpin, perancang', relationship: 'Leo adalah pasangan yang penuh gairah dan kreatif tetapi bisa sangat dominan.' },
    { name: 'Virgo', date: '23 Agustus - 22 September', positive: 'Teliti, analitis, praktis, terorganisir', negative: 'Perfeksionis, terlalu kritis, cenderung menganalisis', strengths: 'Detail-oriented, analitis, praktis', weaknesses: 'Perfeksionis, terlalu kritis, kekakuan', career: 'Penulis, ilmuwan, dokter, akuntan', relationship: 'Virgo adalah pasangan yang praktis dan terorganisir tetapi bisa terlalu kritis.' },
    { name: 'Libra', date: '23 September - 22 Oktober', positive: 'Diplomatis, adil, ramah, idealis', negative: 'Tidak tegas, cenderung menyenangkan, tidak konsisten', strengths: 'Diplomatis, adil, berempati', weaknesses: 'Tidak tegas, terlalu cenderung menyenangkan, tidak konsisten', career: 'Desainer, pengacara, diplomat, konsultan', relationship: 'Libra sangat memperhatikan keseimbangan dalam hubungan dan cenderung ramah tetapi bisa tidak tegas.' },
    { name: 'Scorpio', date: '23 Oktober - 21 November', positive: 'Intens, bersemangat, setia, kuat', negative: 'Cemburu, pendendam, terlalu rahasia', strengths: 'Intens, bersemangat, setia', weaknesses: 'Cemburu, pendendam, terlalu rahasia', career: 'Peneliti, psikolog, detektif, pengacara', relationship: 'Scorpio adalah pasangan yang intens dan setia tetapi bisa sangat cemburu.' },
    { name: 'Sagittarius', date: '22 November - 21 Desember', positive: 'Petualang, optimis, jujur, bebas', negative: 'Tidak sabar, terlalu blak-blakan, ceroboh', strengths: 'Petualang, optimis, jujur', weaknesses: 'Tidak sabar, ceroboh, terlalu blak-blakan', career: 'Pengembara, guru, penulis, pengusaha', relationship: 'Sagitarius adalah pasangan yang petualang dan optimis tetapi bisa sangat blak-blakan.' },
    { name: 'Capricorn', date: '22 Desember - 19 Januari', positive: 'Bertanggung jawab, ambisius, disiplin, praktis', negative: 'Kaku, pesimis, terlalu ambisius', strengths: 'Ambisius, disiplin, praktis', weaknesses: 'Kaku, pesimis, terlalu ambisius', career: 'Manajer, akuntan, insinyur, arsitek', relationship: 'Capricorn adalah pasangan yang ambisius dan bertanggung jawab tetapi bisa sangat kaku.' },
    { name: 'Aquarius', date: '20 Januari - 18 Februari', positive: 'Inovatif, independen, humanis, cerdas', negative: 'Jarak, terlalu eksentrik, sulit dipahami', strengths: 'Inovatif, independen, cerdas', weaknesses: 'Eksentrik, sulit dipahami, cenderung terpisah', career: 'Ilmuwan, penulis, teknologi, aktivis', relationship: 'Aquarius adalah pasangan yang inovatif dan independen tetapi bisa sangat eksentrik.' },
    { name: 'Pisces', date: '19 Februari - 20 Maret', positive: 'Empatik, intuitif, kreatif, penuh kasih', negative: 'Mudah terluka, terlalu sensitif, melamun', strengths: 'Empatik, intuitif, kreatif', weaknesses: 'Mudah terluka, terlalu sensitif, melamun', career: 'Artis, musisi, konselor, perawat', relationship: 'Pisces adalah pasangan yang penuh kasih dan kreatif tetapi bisa sangat sensitif.' },
  ];

  const handleCardClick = (zodiac) => {
    setSelectedZodiac(zodiac);
  };

  const handleClose = () => {
    setSelectedZodiac(null);
  };

  return (
    <Container sx={{ padding: 4 }}>
      <Typography variant="h3" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
        Zodiac Personality Traits
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        This menu provides a complete guide to the personality and characteristics of each zodiac sign. By understanding the strengths and weaknesses of each zodiac sign, users can better understand themselves and those around them, and improve their interpersonal relationships.
      </Typography>

      <Grid container spacing={3}>
        {zodiacData.map(zodiac => (
          <Grid item xs={12} sm={6} md={4} key={zodiac.name}>
            <StyledCard onClick={() => handleCardClick(zodiac)}>
              <StyledCardMedia
                component="img"
                image={zodiacImages[zodiac.name]}
                alt={zodiac.name}
              />
              <CardContent>
                <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif' }}>{zodiac.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  {zodiac.date}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(selectedZodiac)} onClose={handleClose} fullWidth maxWidth="md">
        {selectedZodiac && (
          <Fade in={Boolean(selectedZodiac)}>
            <div>
              <DialogTitle>{selectedZodiac.name} ({selectedZodiac.date})</DialogTitle>
              <StyledDialogContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Positif:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.positive}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Negatif:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.negative}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Kekuatan:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.strengths}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Kelemahan:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.weaknesses}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Karier:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.career}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>Hubungan:</Typography>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{selectedZodiac.relationship}</Typography>
              </StyledDialogContent>
              <CardActions>
                <Button onClick={handleClose}>Close</Button>
              </CardActions>
            </div>
          </Fade>
        )}
      </Dialog>
    </Container>
  );
};

export default ZodiacPersonality;
